import React, { useState, useEffect } from 'react';
import { Shield, CheckCircle, User, RefreshCw } from 'lucide-react';
import { API } from '../../utils/apiConfig';

const AdminVerificationsTab = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);

  const fetchPending = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    setLoading(true);
    try {
      const resp = await fetch(`${API}/admin/users/pending-verifications`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error('Failed');
      const data = await resp.json();
      setUsers(data.users || []);
    } catch (err) {
      console.error('Failed to fetch pending verifications:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPending(); }, []);

  const handleVerify = async (userId) => {
    const token = localStorage.getItem('token');
    if (!token) return;
    setActionLoading(userId);
    try {
      const resp = await fetch(`${API}/admin/users/${userId}/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ verified: true, provider: 'admin_manual' }),
      });
      if (!resp.ok) throw new Error('Failed');
      // Remove from list
      setUsers(prev => prev.filter(u => u.id !== userId));
    } catch (err) {
      console.error('Verification failed:', err);
      alert('Verifizierung fehlgeschlagen');
    } finally {
      setActionLoading(null);
    }
  };

  return (
    <div data-testid="admin-verifications-tab">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
        <h2 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '1.15rem', fontWeight: 600, margin: 0 }}>
          <Shield size={20} />
          Ausstehende Verifizierungen ({users.length})
        </h2>
        <button
          onClick={fetchPending}
          disabled={loading}
          data-testid="refresh-verifications"
          style={{
            display: 'flex', alignItems: 'center', gap: '0.35rem',
            padding: '0.4rem 0.75rem', borderRadius: '8px', border: '1px solid #e2e8f0',
            background: 'white', cursor: 'pointer', fontSize: '0.85rem', color: '#64748b',
            transition: 'all 0.2s',
          }}
        >
          <RefreshCw size={14} className={loading ? 'spin-icon' : ''} />
          Aktualisieren
        </button>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '2rem', color: '#94a3b8' }}>Laden...</div>
      ) : users.length === 0 ? (
        <div
          data-testid="no-pending-verifications"
          style={{
            textAlign: 'center', padding: '3rem', color: '#16a34a', fontWeight: 600,
            background: '#f0fdf4', borderRadius: '12px', border: '1px solid #bbf7d0',
          }}
        >
          <CheckCircle size={32} style={{ marginBottom: '0.5rem' }} />
          <div>Alle Benutzer sind verifiziert!</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {users.map((u) => (
            <div
              key={u.id}
              data-testid={`pending-user-${u.id}`}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '0.75rem 1rem', borderRadius: '10px', border: '1px solid #e2e8f0',
                background: 'white', gap: '1rem',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flex: 1 }}>
                <div style={{
                  width: '40px', height: '40px', borderRadius: '50%', background: '#f1f5f9',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>
                  <User size={20} style={{ color: '#94a3b8' }} />
                </div>
                <div>
                  <div style={{ fontWeight: 600, fontSize: '0.95rem', color: '#0f172a' }}>{u.name}</div>
                  <div style={{ fontSize: '0.8rem', color: '#64748b' }}>{u.email} · {u.role}</div>
                </div>
              </div>
              <button
                onClick={() => handleVerify(u.id)}
                disabled={actionLoading === u.id}
                data-testid={`verify-btn-${u.id}`}
                style={{
                  display: 'flex', alignItems: 'center', gap: '0.35rem',
                  padding: '0.5rem 1rem', borderRadius: '8px', border: 'none',
                  background: '#16a34a', color: 'white', fontWeight: 600,
                  cursor: 'pointer', fontSize: '0.85rem', transition: 'all 0.2s',
                  opacity: actionLoading === u.id ? 0.6 : 1,
                  whiteSpace: 'nowrap',
                }}
              >
                <CheckCircle size={16} />
                {actionLoading === u.id ? 'Wird verifiziert...' : 'Jetzt verifizieren'}
              </button>
            </div>
          ))}
        </div>
      )}

      <style>{`
        .spin-icon { animation: spin-refresh 1s linear infinite; }
        @keyframes spin-refresh { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
};

export default AdminVerificationsTab;
